// Pure form-state model + serialisers for the purchase-order create/edit
// modal. Kept as a dependency-light leaf module (no React, no api-client) so
// the serialisation/validation logic is unit-testable in isolation and so
// extracting it does not couple any vi.mock() factory to this file.
//
// The modal edits money as text (so a half-typed "12." is a valid interim
// state); these helpers parse those strings into the numeric wire shape the
// backend schema expects and drop empty optional fields rather than sending
// empty strings (the backend rejects `""` on its `.min(1).optional()` fields).
// Existing line `id`s are preserved on edit so a stored line survives
// renumbering — the split rows on any recorded invoice keep pointing at it.
//
// A PO line carries no single item/milestone/SOW/supplier — it tags zero or
// more rate-card items, milestones and SOWs (`rateItemIds`/`milestoneIds`/
// `sowIds`), plus its own `gstApplies` flag and a signed `adjustments` ledger
// (opening balances and SAP true-ups) for manual balance corrections. GST is
// decided per line, not at the PO header, so `PoFormState` carries no
// `supplierName`/`gstApplies` of its own.
//
// validatePoForm's per-line message and the offending control are decided in
// the same branch chain, so `lineErrorFields` (same keys as `lineErrors`)
// always names exactly the field the message is about — the UI layer uses it
// to mark the failing control (aria-invalid) without re-deriving the check.

import { parseNumericText } from "../../../utils/numericText";
import type { PoLineType, PurchaseOrderStatus } from "../../../types/finance";
import type {
  CreatePurchaseOrderInput,
  LineAdjustmentInput,
  PoLineInput,
  PurchaseOrderWithDerived,
  UpdatePurchaseOrderInput,
} from "../../../types/finance";

// Stable client-only keys for React list identity — never sent to the backend.
let keyCounter = 0;
function nextKey(): string {
  keyCounter += 1;
  return `poline-${keyCounter}`;
}

export interface LineAdjustmentDraft {
  /** Client-only React key; not part of the wire payload. */
  key: string;
  /** Stored adjustment id, preserved on edit. */
  id?: string;
  amount: string;
  label: string;
  note: string;
  effectiveDate: string;
}

export interface PoLineDraft {
  /** Client-only React key; not part of the wire payload. */
  key: string;
  /** Stored line id, preserved on edit so splits survive renumbering. */
  id?: string;
  lineNumber: string;
  lineType: PoLineType;
  description: string;
  unitPrice: string;
  quantity: string;
  lineTotal: string;
  /** While true, lineTotal tracks unitPrice × quantity. A manual edit to the
   *  total clears this so a bespoke override is never clobbered. */
  autoTotal: boolean;
  gstApplies: boolean;
  rateItemIds: string[];
  milestoneIds: string[];
  sowIds: string[];
  adjustments: LineAdjustmentDraft[];
}

export interface PoFormState {
  poNumber: string;
  description: string;
  totalValue: string;
  status: PurchaseOrderStatus;
  issuedDate: string;
  lines: PoLineDraft[];
}

/** Which of a line's controls failed validation — values are exactly the
 *  poLineFieldId suffixes so callers can address the failing input. */
export type PoLineErrorField = "line-number" | "description" | "line-total";

export interface PoFieldErrors {
  poNumber?: string;
  totalValue?: string;
  /** Form-level "need at least one line". */
  lines?: string;
  /** Form-level "line totals must equal the PO total value". */
  reconciliation?: string;
  /** Per-line messages, keyed by the draft's client key. */
  lineErrors: Record<string, string>;
  /** Which control each errored line failed on. Same keys as lineErrors. */
  lineErrorFields: Record<string, PoLineErrorField>;
}

// Next default line number in the 10/20/30… sequence.
export function nextLineNumber(lines: PoLineDraft[]): string {
  return String((lines.length + 1) * 10);
}

export function makeEmptyAdjustment(): LineAdjustmentDraft {
  return {
    key: nextKey(),
    amount: "",
    label: "",
    note: "",
    effectiveDate: "",
  };
}

export function makeEmptyLine(lineNumber: string): PoLineDraft {
  return {
    key: nextKey(),
    lineNumber,
    lineType: "standard",
    description: "",
    unitPrice: "",
    quantity: "",
    lineTotal: "",
    autoTotal: true,
    gstApplies: true,
    rateItemIds: [],
    milestoneIds: [],
    sowIds: [],
    adjustments: [],
  };
}

export function buildEmptyPoFormState(): PoFormState {
  return {
    poNumber: "",
    description: "",
    totalValue: "",
    status: "open",
    issuedDate: "",
    lines: [makeEmptyLine("10")],
  };
}

// Parse a money text field, tolerating comma group separators (values pasted
// from SAP arrive as "1,234,567.89"). Returns null for blank/unparseable input
// so the caller can decide whether that is an error or an omitted optional.
export function parseMoney(value: string): number | null {
  return parseNumericText(value);
}

// The auto-computed line total (unitPrice × quantity) as a display string, or
// null when either factor is missing/unparseable.
export function computeLineTotalString(
  unitPrice: string,
  quantity: string,
): string | null {
  const up = parseMoney(unitPrice);
  const qty = parseMoney(quantity);
  if (up === null || qty === null) return null;
  return String(Math.round(up * qty * 100) / 100);
}

// Round to whole cents so float artefacts (0.1 + 0.2) never fail an equality
// check the user entered as equal.
function toCents(n: number): number {
  return Math.round(n * 100);
}

// Sum of every line's parsed total; a blank/unparseable total counts as 0.
export function sumLineTotals(lines: PoLineDraft[]): number {
  return lines.reduce((acc, l) => acc + (parseMoney(l.lineTotal) ?? 0), 0);
}

// Apply an edit to a single line, keeping the auto-total invariant: editing a
// factor recomputes the total while autoTotal holds; editing the total itself
// switches the line to a manual override.
export function updateLine(
  line: PoLineDraft,
  patch: Partial<PoLineDraft>,
): PoLineDraft {
  const next: PoLineDraft = { ...line, ...patch };
  if ("lineTotal" in patch) {
    next.autoTotal = false;
    return next;
  }
  if (("unitPrice" in patch || "quantity" in patch) && next.autoTotal) {
    const auto = computeLineTotalString(next.unitPrice, next.quantity);
    if (auto !== null) next.lineTotal = auto;
  }
  return next;
}

/** Per-family maps of tag ids used by every line EXCEPT the one at `index`,
 *  each id mapped to a short owning-line hint ("Line 20"). Drives the
 *  used-elsewhere greying in the line editors' tag pickers, so tagging a
 *  milestone/SOW on a second line (a variation splitting its payment) is a
 *  visible, deliberate choice rather than an accident.
 *  On duplicates across several other lines the first owning line wins;
 *  a blank line number falls back to the line's 1-based position. */
export interface TagsUsedOnOtherLines {
  rateItem: Map<string, string>;
  milestone: Map<string, string>;
  sow: Map<string, string>;
}

export function tagsUsedOnOtherLines(
  lines: PoLineDraft[],
  index: number,
): TagsUsedOnOtherLines {
  const maps: TagsUsedOnOtherLines = {
    rateItem: new Map(),
    milestone: new Map(),
    sow: new Map(),
  };
  lines.forEach((line, i) => {
    if (i === index) return;
    const hint = `Line ${line.lineNumber.trim() || i + 1}`;
    const families: Array<[Map<string, string>, string[]]> = [
      [maps.rateItem, line.rateItemIds],
      [maps.milestone, line.milestoneIds],
      [maps.sow, line.sowIds],
    ];
    for (const [map, ids] of families) {
      for (const id of ids) {
        if (!map.has(id)) map.set(id, hint);
      }
    }
  });
  return maps;
}

export function stateFromPurchaseOrder(
  po: PurchaseOrderWithDerived,
): PoFormState {
  return {
    poNumber: po.poNumber,
    description: po.description ?? "",
    totalValue: String(po.totalValue),
    status: po.status,
    issuedDate: po.issuedDate ?? "",
    lines: po.lines.map((l) => {
      const unitPrice = l.unitPrice !== undefined ? String(l.unitPrice) : "";
      const quantity = l.quantity !== undefined ? String(l.quantity) : "";
      const auto = computeLineTotalString(unitPrice, quantity);
      return {
        key: nextKey(),
        id: l.id,
        lineNumber: l.lineNumber,
        lineType: l.lineType,
        description: l.description,
        unitPrice,
        quantity,
        lineTotal: String(l.lineTotal),
        // Track factors only when they actually reproduce the stored total —
        // otherwise a bespoke total would be silently overwritten on the first
        // factor edit.
        autoTotal: auto !== null && Number(auto) === l.lineTotal,
        gstApplies: l.gstApplies,
        rateItemIds: [...l.rateItemIds],
        milestoneIds: [...l.milestoneIds],
        sowIds: [...l.sowIds],
        adjustments: l.adjustments.map((a) => ({
          key: nextKey(),
          id: a.id,
          amount: String(a.amount),
          label: a.label ?? "",
          note: a.note ?? "",
          effectiveDate: a.effectiveDate ?? "",
        })),
      };
    }),
  };
}

export function validatePoForm(state: PoFormState): PoFieldErrors {
  const errors: PoFieldErrors = { lineErrors: {}, lineErrorFields: {} };
  if (!state.poNumber.trim()) errors.poNumber = "PO number is required";
  const total = parseMoney(state.totalValue);
  if (total === null || total <= 0) {
    errors.totalValue = "Total value must be greater than zero";
  }
  if (state.lines.length === 0) {
    errors.lines = "Add at least one budget line";
  }
  for (const line of state.lines) {
    const lineTotal = parseMoney(line.lineTotal);
    if (!line.lineNumber.trim()) {
      errors.lineErrors[line.key] = "Line number is required";
      errors.lineErrorFields[line.key] = "line-number";
    } else if (!line.description.trim()) {
      errors.lineErrors[line.key] = "Description is required";
      errors.lineErrorFields[line.key] = "description";
    } else if (lineTotal === null || lineTotal < 0) {
      errors.lineErrors[line.key] = "Line total must be a non-negative amount";
      errors.lineErrorFields[line.key] = "line-total";
    }
  }
  // Hard reconciliation: the budget lines must sum to the PO total value (both
  // ex-GST). Only checked once the PO total is a valid positive number and no
  // per-line total is itself invalid — those field errors surface first so the
  // reconciliation message is never noise on an incomplete form.
  const hasLineTotalError = Object.keys(errors.lineErrors).length > 0;
  if (total !== null && total > 0 && !hasLineTotalError) {
    if (toCents(sumLineTotals(state.lines)) !== toCents(total)) {
      errors.reconciliation = "Budget lines must add up to the PO total value.";
    }
  }
  return errors;
}

export function hasErrors(errors: PoFieldErrors): boolean {
  return (
    !!errors.poNumber ||
    !!errors.totalValue ||
    !!errors.lines ||
    !!errors.reconciliation ||
    Object.keys(errors.lineErrors).length > 0
  );
}

// Serialise a single adjustment draft, returning null for a blank amount so
// the caller can drop it from the wire payload rather than send a zero.
function serialiseAdjustment(
  adjustment: LineAdjustmentDraft,
): LineAdjustmentInput | null {
  const amount = parseMoney(adjustment.amount);
  if (amount === null) return null;
  const out: LineAdjustmentInput = { amount };
  if (adjustment.id) out.id = adjustment.id;
  if (adjustment.label.trim()) out.label = adjustment.label.trim();
  if (adjustment.note.trim()) out.note = adjustment.note.trim();
  if (adjustment.effectiveDate.trim()) {
    out.effectiveDate = adjustment.effectiveDate.trim();
  }
  return out;
}

function serialiseLine(line: PoLineDraft): PoLineInput {
  const out: PoLineInput = {
    lineNumber: line.lineNumber.trim(),
    description: line.description.trim(),
    lineType: line.lineType,
    gstApplies: line.gstApplies,
    lineTotal: parseMoney(line.lineTotal) ?? 0,
    rateItemIds: [...line.rateItemIds],
    milestoneIds: [...line.milestoneIds],
    sowIds: [...line.sowIds],
    adjustments: line.adjustments
      .map(serialiseAdjustment)
      .filter((a): a is LineAdjustmentInput => a !== null),
  };
  if (line.id) out.id = line.id;
  const unitPrice = parseMoney(line.unitPrice);
  if (unitPrice !== null) out.unitPrice = unitPrice;
  const quantity = parseMoney(line.quantity);
  if (quantity !== null) out.quantity = quantity;
  return out;
}

export function toCreateInput(state: PoFormState): CreatePurchaseOrderInput {
  const input: CreatePurchaseOrderInput = {
    poNumber: state.poNumber.trim(),
    totalValue: parseMoney(state.totalValue) ?? 0,
    status: state.status,
    lines: state.lines.map(serialiseLine),
  };
  if (state.description.trim()) input.description = state.description.trim();
  if (state.issuedDate.trim()) input.issuedDate = state.issuedDate.trim();
  return input;
}

export function toUpdateInput(
  state: PoFormState,
  version: number,
): UpdatePurchaseOrderInput {
  return { ...toCreateInput(state), version };
}
